"use client";

import { getToolName } from "ai";
import { type ReactElement } from "react";
import type { ChatToolPartPlugin } from "@/lib/ai/chat-client-plugin";
import type { AgentUIMessage } from "@/lib/ai/types";
import type { VizStreamStateApi } from "@/lib/ai/visualization/client/hooks/use-viz-stream";
import { VizToolPartRenderer } from "@/lib/ai/visualization/client/hooks/viz-tool-part-renderer";
import {
  buildVizToolPartHistoryState,
  createVizToolPartMessageStateResolver,
  shouldHideVizToolPart,
  toVizToolPartMessageState,
  type VizToolPartHistoryState,
  type VizToolPartMessageState,
} from "@/lib/ai/visualization/client/hooks/viz-tool-part-state";
import { VIZ_TOOL_NAME } from "@/lib/ai/visualization/config";
import {
  buildVisualizationHistoryModel,
  buildVisualizationHistoryModelFromScannedParts,
} from "@/lib/ai/visualization/history";
import {
  resolveVizRenderToolResult,
  scanVisualizationMessageParts,
} from "@/lib/ai/visualization/shared/transport";

type CreateVizToolPartPluginArgs = {
  messages: AgentUIMessage[];
  vizStream?: VizStreamStateApi;
  vizToolTitle: string;
};

type VizToolPartPluginResolver = (
  messages: AgentUIMessage[],
) => ChatToolPartPlugin;

/**
 * `viz_render` 専用の tool-part plugin を構築する。
 *
 * 目的:
 * - ChatMessages から可視化専用ロジックを切り離し、plugin 経由で描画する
 * - 既存の重複抑止・stream描画・datasetイベント解決を保持する
 * - タイトル文言の解決も内包し、呼び出し側の引数契約を最小化する
 *
 * 失敗時挙動:
 * - 不正 state は描画抑止せず、既存の ToolOutput 表示へ委譲する
 *
 * @remarks
 * AI SDK の推奨どおり message `parts` を基準に tool part を描画する。
 * `data-viz-patch` は `onData` 経由の transient event を `useVizStream` で受け取り、
 * dataset raw は message history の source tool output から常に再構築する。
 */
export function createVizToolPartPlugin({
  messages,
  vizStream,
  vizToolTitle,
  historyState = buildVizToolPartHistoryState(
    buildVisualizationHistoryModel(messages),
  ),
}: CreateVizToolPartPluginArgs & {
  historyState?: VizToolPartHistoryState;
}): ChatToolPartPlugin {
  const resolveMessageState = createVizToolPartMessageStateResolver();

  return {
    placement: "activity-and-standalone",
    toolCallTitle: vizToolTitle,
    getMessageState: (message: AgentUIMessage): VizToolPartMessageState =>
      resolveMessageState(message),
    shouldHidePart: ({ part, partIndex, message, messageState }) => {
      if (getToolName(part) !== VIZ_TOOL_NAME) {
        return false;
      }
      const state = toVizToolPartMessageState(messageState);
      return shouldHideVizToolPart({
        part,
        isDuplicateOutputPart: historyState.duplicateVizPartKeySet.has(
          `${message.id}:${partIndex}`,
        ),
        vizOutputToolCallIds: state.vizOutputToolCallIds,
      });
    },
    tryRender: ({
      part,
      partIndex,
      message,
      chatStatus,
      isLastMessage,
      messageState,
    }): ReactElement | null => {
      if (getToolName(part) !== VIZ_TOOL_NAME) {
        return null;
      }
      const result = resolveVizRenderToolResult(part.output);
      const state = toVizToolPartMessageState(messageState);
      const toolCallId = part.toolCallId;
      const hasRenderableFallback =
        result?.outcome === "fallback" &&
        typeof toolCallId === "string" &&
        historyState.vizOutputIndex.has(toolCallId);
      if (
        part.state === "output-error" ||
        (result && result.outcome !== "success" && !hasRenderableFallback)
      ) {
        // Non-success results stay inside the generic Process details row.
        // A validated table substitution is still rendered as useful output.
        return null;
      }
      return (
        <VizToolPartRenderer
          part={part}
          message={message}
          chatStatus={chatStatus}
          isLastMessage={isLastMessage}
          historyDatasetIndex={historyState.datasetIndex}
          isDuplicateOutputPart={historyState.duplicateVizPartKeySet.has(
            `${message.id}:${partIndex}`,
          )}
          vizOutputToolCallIds={state.vizOutputToolCallIds}
          historyVizOutputIndex={historyState.vizOutputIndex}
          vizStream={vizStream}
        />
      );
    },
  };
}

/**
 * 可視化に関係する履歴差分だけで plugin を差し替える resolver を作る。
 *
 * @remarks
 * assistant の text / reasoning が streaming で置き換わっても、source tool と
 * viz output が不変なら同じ plugin を返し、既存 chart 行の再描画を抑える。
 */
export function createVizToolPartPluginResolver({
  vizStream,
  vizToolTitle,
}: Omit<CreateVizToolPartPluginArgs, "messages">): VizToolPartPluginResolver {
  let cachedHistorySignature: string | null = null;
  let cachedPlugin: ChatToolPartPlugin | null = null;

  return (messages) => {
    const scannedParts = scanVisualizationMessageParts(messages);
    const historyState = buildVizToolPartHistoryState(
      buildVisualizationHistoryModelFromScannedParts(scannedParts),
    );
    const historySignature = historyState.historySignature;
    if (cachedPlugin && cachedHistorySignature === historySignature) {
      return cachedPlugin;
    }

    cachedHistorySignature = historySignature;
    cachedPlugin = createVizToolPartPlugin({
      messages,
      historyState,
      vizStream,
      vizToolTitle,
    });
    return cachedPlugin;
  };
}
